/**
 * we don't use @types/ol since it does not contain all functions in ol
 * use ol packages also make final file size more compact
 */
import i18n from "@/common/filters";

import Map from "ol/Map";
import Control from "ol/control/Control";
import Draw from "ol/interaction/Draw";
import Geometry from "ol/geom/Geometry";
import GeometryType from "ol/geom/GeometryType";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import Polygon from "ol/geom/Polygon";
import Feature from "ol/Feature";
import Overlay from "ol/Overlay";
import Interaction from "ol/interaction/Interaction";
import Snap from "ol/interaction/Snap";
import Collection from "ol/Collection";
import Projection from "ol/proj/Projection";
import OverlayPositioning from "ol/OverlayPositioning";
import { EventsKey } from "ol/events";
import { unByKey } from "ol/Observable";

import { UTM } from "../utm";
import { IDrawOptions, IMeasureResult, Coordinate } from "../types";

interface ViewState {
  center: Coordinate;
  projection: Projection;
  resolution: number;
  rotation: number;
}

const MEASURE_TYPE = {
  coordinate: GeometryType.POINT,
  distance: GeometryType.LINE_STRING,
  area2: GeometryType.POLYGON,
  Point: GeometryType.POINT,
  LineString: GeometryType.LINE_STRING,
  Polygon: GeometryType.POLYGON
}

export class Measurer extends Control {
  //following four are private in Control
  private map_: Map;
  private target_: HTMLElement;
  //private element: HTMLElement;

  private draw_: Draw;
  private drawOptions_: IDrawOptions;//  {[key:string] : any};
  private sketch_: Feature;
  private geomChangeEventKey_: EventsKey;
  private drawUndoEventKey_: EventsKey;

  private labels_: Array<Feature>;
  private labelIndex_: number = 0; // IMPORTANT

  private measureTooltip_: Overlay;
  private measureTooltipElement_: HTMLElement;
  private measureTooltipCoordinate_: Coordinate;

  private unit_: "metric" | "thai" = 'metric';
  private crs_: "WGS84" | "UTM" = 'WGS84';

  constructor(options) {
    super(options);
    this.element = document.createElement('DIV');
    this.element.className = 'ol-measurement-value';
    if (options) {
      this.drawOptions_ = {
        type: GeometryType.LINE_STRING
      };
      [ 'clickTolerance','features','source','snapTolerance','type','maxPoints',
        'minPoints','finishCondition','style','geometryFunction','geometryName',
        'condition','freehand','freehandCondition','wrapX'
      ].forEach((o) => {
        if (options[o]) {
          if (o=='type') {
            this.drawOptions_[o] = MEASURE_TYPE[options[o]] || GeometryType.LINE_STRING;
          } else {
            this.drawOptions_[o] = options[o];
          }
        }
      });
      if (options.unit) {
        this.unit_ = options.unit;
      }
      if (options.crs) {
        this.crs_ = options.crs;
      }
    }
    this.createDraw();
  }

  private createDraw() {
    this.draw_ = new Draw(this.drawOptions_);
    /* IMPORTANT to use self instead of "this" since "this" in the callback
     * is the Drawer not this Measurer. 
     * Therefore, in the callback, this.clearMeasurer() is undefined
     */
    let self = this; 
    this.draw_.on('drawstart', (event) => {
      self.startDraw(event);
    });
    this.draw_.on('drawend', () => {
      self.endDraw();
    });
    this.showMeasureResult({
      value: null,
      content: i18n.L('sClickMapToMeasure')
    });
  }

  private recreateDraw() {
    this.endDraw();
    this.clearMeasure();
    if (this.map_) {
      this.draw_.un('drawstart', this.startDraw);
      this.draw_.un('drawend', this.endDraw);
      this.map_.removeInteraction(this.draw_);
      this.createDraw();
      this.addToInteraction(this.draw_);
    }
  }

  /**
   * All Snap interactions must be added after the Modify and Draw interactions
   * in order for its map browser event handlers to be fired first. Its handlers
   * are responsible of doing the snapping.
   */
  private addToInteraction(draw) {
    let interactions: Collection<Interaction> = this.map_.getInteractions();
    let inserted:boolean = false;
    let n = interactions.getLength();
    for (let i=0; i<n; i++) {
      if (interactions.item(i) instanceof Snap) {
        interactions.insertAt(i, draw);
        inserted = true;
        break;
      }
    }
    if (!inserted) {
      this.map_.addInteraction(draw);
    }
  }

  private createMeasureTooltip() {
    if (this.measureTooltipElement_) {
      this.measureTooltipElement_.parentNode.removeChild(this.measureTooltipElement_);
    }
    this.measureTooltipElement_ = document.createElement('div');
    this.measureTooltipElement_.className = 'tooltip tooltip-measure';
    this.measureTooltip_ = new Overlay({
      element: this.measureTooltipElement_,
      offset: [0, -15],
      positioning: OverlayPositioning.BOTTOM_CENTER
    });
    this.map_.addOverlay(this.measureTooltip_);
  }

  private clearMeasure() {
    if (this.measureTooltip_) {
      if (this.measureTooltipElement_) {
        this.measureTooltipElement_.parentNode.removeChild(this.measureTooltipElement_);
        this.measureTooltipElement_ = null;
      }
      if (this.map_) {
        this.map_.removeOverlay(this.measureTooltip_);
      }
      this.measureTooltip_ = null;
    }
    this.drawOptions_.source.clear();
    this.labels_ = null;
    this.labelIndex_ = 0;
  }

  startDraw(evt) {
    this.clearMeasure();
    // set sketch
    this.sketch_ = evt.feature;

    let geom = this.sketch_.getGeometry();
    let tooltipCoord = evt.coordinate;
    let result: IMeasureResult;
    if (geom instanceof Point) {
      this.labels_ = null;
      this.createMeasureTooltip();
      result = this.measurePoint(geom);
      this.showMeasureResult(result);
      tooltipCoord = geom.getLastCoordinate();
      this.measureTooltipElement_.innerHTML = result.content;
      this.measureTooltip_.setPosition(tooltipCoord);
    } else {
      if (geom instanceof Polygon) {
        this.labels_ = [null,null];
      } else if (geom instanceof LineString) {
        this.labels_ = [null];
      }
      let self = this;
      this.geomChangeEventKey_ = this.sketch_.getGeometry().on('change', (evt) => {
        let geom = evt.target;
        let output;
        if (geom instanceof Polygon) {
          self.measureArea();
          self.measureSegmentedDistance(geom);
        } else if (geom instanceof LineString) {
          result = self.measureDistance(geom);
          self.showMeasureResult(result);
          self.measureSegmentedDistance(geom);
          if (geom.getCoordinates().length>2) {
            if (!self.measureTooltip_) {
              self.createMeasureTooltip();
            }
            self.measureTooltipElement_.innerHTML = result.content;
            tooltipCoord = geom.getLastCoordinate();
            self.measureTooltip_.setPosition(tooltipCoord);
          }
        }
      });
      let mapElem:Element = this.map_.getTargetElement();
      mapElem.addEventListener('keyup', this.undoRedo_);
    }
  };

  endDraw() {
    this.sketch_ = null;
    this.labels_ = null;
    this.labelIndex_ = 0;
    
    if (this.measureTooltipElement_) {
      this.measureTooltipElement_.className = 'tooltip tooltip-static';
    }
    if (this.measureTooltip_) {
      this.measureTooltip_.setOffset([0, -10]);
    }

    // reset feature style to layer default style
    let features = this.drawOptions_.source.getFeatures();
    this.drawOptions_.source.clear();
    features.forEach((f) => {
      f.setStyle(null);
    });
    this.drawOptions_.source.addFeatures(features);

    unByKey(this.geomChangeEventKey_);
    let mapElem = this.map_.getTargetElement();
    mapElem.removeEventListener('keyup', this.undoRedo_);
  }

  /**
   * Undo or Redo
   * Undo when press esc or ctrl^Z key
   * Redo when press ctrl^Y
   * @param event 
   */
  private undoRedo_ = (event) => {
    this.undoRedoDraw(event);
  }

  private discardDraw() {
    if (this.draw_['abortDrawing_']) {
      this.draw_['abortDrawing_']();
    }
    this.clearMeasure();
    this.endDraw();
  }

  private undoPoints:[[number,number]];
  undoRedoDraw(event) {
    if (!this.sketch_) {
      return;
    }
    let keyCode = event.keyCode || event.which;
    let ctrlKey = event.ctrlKey;
    if (keyCode==27) {
      this.discardDraw();
    } else if (ctrlKey) {
      let geom:Geometry = this.sketch_.getGeometry();
      let coords:Array<Coordinate>;
      if (keyCode==90) { // ctrl^Z
        if (geom instanceof LineString) {
          coords = geom.getCoordinates();
          if (coords.length <= 2) {
            this.discardDraw();
            return;
          }
        } else if (geom instanceof Polygon) {
          coords = geom.getLinearRing(0).getCoordinates();
          if (coords.length <= 3) {
            this.discardDraw();
            return;
          }
        }
        let source = this.drawOptions_.source;
        this.labelIndex_--;
        source.removeFeature(source.getFeatures()[this.labelIndex_ - this.labels_.length]);
        this.draw_.removeLastPoint();
      } else if (keyCode==89) { // ctrl^Y

      }
    }
  }

  calcArea(geom:Polygon) : number {
    let sm = this.map_['SOFTOO_MAP'];
    return sm.calcArea(geom);
  }
  
  calcLength(geom:LineString) : number {
    let sm = this.map_['SOFTOO_MAP'];
    return sm.calcLength(geom);
  };
  
  measureArea(feature?:Feature) : IMeasureResult {
    let result: IMeasureResult;
    if (this.unit_=='thai') {
      result = this.measureAreaThai(feature);
    } else {
      result = this.measureAreaMetric(feature);
    }
    this.showMeasureResult(result);
    return result;
  }

  measureAreaMetric(feature?:Feature) : IMeasureResult {
    feature = feature || this.sketch_;
    let result: IMeasureResult;
    let area = this.calcArea(<Polygon>feature.getGeometry());
    if (area > 10000) {
      result = {
        value: Math.round(area / 1000000 * 100) / 100,
        unit: i18n.L('unitSQKM')
      };
    } else {
      result = {
        value: Math.round(area * 100) / 100,
        unit: i18n.L('unitSQM')
      };
    }
    result.content = (<number>result.value).toThousandCommaString() + ' ' + result.unit;
    feature.set('labelStyle', {
      text: (result.value>0) ? result.content : '',
      font: '13px sans-serif',
      exceedLength: true,
      offsetX: 0,
      offsetY: 0
    });
    return result;
  }

  measureAreaThai(feature?:Feature) : IMeasureResult {
    feature = feature || this.sketch_;
    let area:number = this.calcArea(<Polygon>feature.getGeometry());
    let rai:number,ngan:number,wah:number;
    area = area / 1600.0; //to rai
    rai = Math.trunc(area);
    area = (area - rai) * 4.0; //to ngan
    ngan = Math.trunc(area);
    area = (area - ngan) * 100.0; //to sqwah
    wah = Math.round(area);
    if (wah>=100) {
      wah -= 100;
      ngan += 1;
      if (ngan>=0) {
        ngan -= 1;
        rai += 1;
      }
    }

    let result: IMeasureResult;
    let units = [
      i18n.L('unitRai'),
      i18n.L('unitNgan'),
      i18n.L('unitWah')
    ];
    result = {
      value: [rai, ngan, wah],
      unit: units.join('-')
    }
    let vs = (<[number]>result.value).map((v)=>{
      return v.toThousandCommaString(0);
    });
    result.content = vs.join('-') + ' (' + result.unit + ')';
    
    let val:string[] = [];
    let unt:string[] = [];
    let i;
    for (i=0; i<3; i++) {
      if ((result.value[i]>0) || ((val.length>0) && i<2)) {
        val.push(result.value[i].toThousandCommaString(0));
        unt.push(units[i]);
      }
    }
    let label:string = '';
    if (val.length == 1) {
      label = val[0] + unt[0] ;
    } else if (val.length>0) {
      label = val.join('-') + '\n' + unt.join('-');
    }
    feature.set('labelStyle', {
      text: label,
      font: '13px sans-serif',
      exceedLength: true,
      offsetX: 0,
      offsetY: 0
    });
    return result;
  }

  measureDistance(geom:LineString) : IMeasureResult {
    let length = this.calcLength(geom);
    let result:IMeasureResult;
    if (length >= 1000) {
      result = {
        value: Math.round(length / 10) / 100,
        unit: i18n.L('unitKM')
      }
    } else {
      result = {
        value: Math.round(length * 100) / 100,
        unit: i18n.L('unitM')
      }
    }
    result.content = result.value + ' ' + result.unit;
    return result;
  }

  measureSegmentedDistance(geom:LineString | Polygon) {
    let labelIndex, coords:Array<Coordinate>;
    let labels = this.labels_;
    if (geom instanceof Polygon) {
      coords = geom.getLinearRing(0).getCoordinates();
    } else {
      coords = geom.getCoordinates();
    }
    labelIndex = coords.length - 1;
    if (labelIndex <= 0) {
      return;
    } else if (labelIndex>this.labelIndex_) {
      //new vertex has been added
      labels[0] = labels[labels.length-1];
      labels[labels.length-1] = null;
      this.labelIndex_ = labelIndex;
    }

    coords.splice(0,(coords.length - (labels.length + 1)));
    let p1:Coordinate, p2:Coordinate;
    let rad:number, dx:number, dy:number;
    let line: LineString;
    let i;
    for (i=0; i<labels.length; i++) {
      p1 = coords[i];
      p2 = coords[i + 1];
      line = new LineString([p1,p2]);
      dx = p2[0] - p1[0];
      dy = p2[1] - p1[1];
      if (dy==0) {
          rad = 0;
      } else if (dx==0) {
          rad = -Math.PI / 2;
      } else {
          rad = -Math.atan2(dy,dx);
          if (dx < 0) {
              rad += Math.PI;
          }
      }
      let result: IMeasureResult = this.measureDistance(line);
      if (result.value > 0) {
        if (!labels[i]) {
          labels[i] = new Feature({
            geometry: line,
            LABEL_ONLY: true,
            labelStyle: {
              text: result.content,
              rotation: rad,
              font: '13px sans-serif',
              offsetY: -10
            }
          });
          this.drawOptions_.source.addFeature(labels[i]);
          labels[i].setStyle(this.drawOptions_.style);
        } else {
          labels[i].setGeometry(line);
          labels[i].set('labelStyle', {
            text: result.content,
            rotation: rad,
            font: '13px sans-serif',
            offsetY: -10
          });
        }
      }
    }
  }

  measurePoint(geom:Point) : IMeasureResult {
    let p:Point = <Point> geom.clone();
    let mPrj = this.map_.getView().getProjection().getCode();
    p.transform(mPrj, 'EPSG:4326');
    let coords = p.getLastCoordinate();
    let result:IMeasureResult;
    switch (this.crs_) {
    case 'WGS84':
      coords.forEach((c,index) => {
        coords[index] = Math.round(c*1000000) / 1000000;
      });
      result = {
        value: coords,
        unit: 'WGS84',
        content: coords[0] + ',' + coords[1]
      };
      break;
    case 'UTM':
      let utm = UTM.lonLatToUTM(coords[0], coords[1]);
      utm.x = Math.round(utm.x * 100) / 100;
      utm.y = Math.round(utm.y * 100) / 100;
      result = {
        value: [utm.x, utm.y],
        unit: 'UTM' + utm.zone + utm.NS,
        content: utm.zone + utm.NS + ':' + utm.x + ',' + utm.y
      };
      break;
    }
    return result;
  }

  showMeasureResult(result:IMeasureResult) {
    this.element.innerHTML = result.content;
  }

  set Type(type:"coordinate" | "distance" | "area2" | "Point" | "LineString" | "Polygon") {
    if (this.map_) {
      //ensure the map will get event, such as for undoDraw
      this.map_.getTargetElement().focus();
    }
    let mType = MEASURE_TYPE[type];
    if (this.drawOptions_.type != mType) {
      this.drawOptions_.type = mType;
      this.recreateDraw();
    }
  }

  set Unit(unit:"metric" | "thai") {
    if (this.map_) {
      //ensure the map will get event, such as for undoDraw
      this.map_.getTargetElement().focus();
    }
    if (this.unit_ != unit) {
      this.unit_ = unit;
      //re-calculate area. not need to recalc distince that is awalys using metric unit
      let result: IMeasureResult;
      let geom:Geometry;
      let feature: Feature;
      feature = this.sketch_;
      if (!feature) {
        let features = this.drawOptions_.source.getFeatures();
        if (features && features.length>0) {
          feature = features[features.length-1];
        }
      }
      if (feature) {
        geom = feature.getGeometry();
        if (geom instanceof Polygon) {
          result = this.measureArea(feature);
        }
      }
    }
  }

  set CRS(crs:"WGS84" | "UTM") {
    if (this.map_) {
      //ensure the map will get event, such as for undoDraw
      this.map_.getTargetElement().focus();
    }
    if (this.crs_ != crs) {
      this.crs_ = crs;
      this.drawOptions_.source.getFeatures().forEach((f) => {
        let result: IMeasureResult;
        let geom:Geometry = f.getGeometry();
        if (geom instanceof Point) {
          result = this.measurePoint(geom);
          this.showMeasureResult(result);
          this.measureTooltipElement_.innerHTML = result.content;
          this.measureTooltip_.setPosition(geom.getLastCoordinate());
        }
      });
    }
  }

  setMap(map:Map) {
    super.setMap(map);
    if (this.map_) {
      this.addToInteraction(this.draw_);
    }
  }

  remove() {
    if (this.map_) {
      this.endDraw();
      this.clearMeasure();
      if (this.draw_) {
        this.map_.removeInteraction(this.draw_);
      }
      this.map_.removeControl(this);
    }
  }
}